import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';
import { getPaginationUrl } from '../../utils/constants/GetPaginationURL';
const initialState = {
  loading: false,
  goalStatusFilterTabLoading: false,
  quarters: [],
  tagNames: [],
  goalData: { data: [], meta: {} },
  goalStatuses: [{ id: -1, status: 'All', count: 0 }],
  fileName: {},
  designations: [],
  skillAndWllUserSpecificData: [],
  kralibraries: [],
  taggedKra: [],
  lastTaggedKraType: null,
  kraUserSpecificData: {},
  alltaggedkra: [],
  evaluationData: [],
  cycleAssignedUsers: [],
  cyclePreData: [],
  cycleStatus: {},
  manageAppraisalData: [],
  selfAppraisalDatas: {
    data: [],
    meta: {},
  },
  formFillingDatas: {},
  appraisalPostLoading: false,
  apprasialCycleListsForManagers: {
    data: [],
    meta: {},
  },
  usersList: [],
  reportingHeadSkillWillKra: [],
  // Goal "Add" block/unblock config.
  // The Config screen's user list is paginated and managed in the component's
  // local state, so only the current user's block flag lives here (used to
  // disable the "Add Goal" button on the Goal tab).
  //
  // Three states on purpose:
  //   null  -> we don't know yet (still fetching). The button stays disabled.
  //   true  -> blocked.
  //   false -> allowed.
  // It starts as null (not false) so the "Add Goal" button never flashes as
  // clickable for a blocked user while their status is still loading.
  currentUserGoalBlocked: null,
  pendingEditRequestCount: 0,
};

export const getAllQuarters = createAsyncThunk('appraisal/getAllQuarters', async (data) => {
  // Add the status filter only when a specific one is chosen (1 = active, 0 = inactive).
  // For "All" we send nothing, and the backend returns active + inactive (not deleted).
  const statusParam =
    data.appraisal_tag_status !== undefined && data.appraisal_tag_status !== ''
      ? `&appraisal_tag_status=${data.appraisal_tag_status}`
      : '';
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-tag-management?search=${data.search}&page=${data.page}&limit=${data.limit}${statusParam}`,
  });
  return res;
});

export const createQuarter = createAsyncThunk('appraisal/createQuarter', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-tag-management`,
    data: data,
  });
  return res;
});

export const deleteQuarter = createAsyncThunk('appraisal/deleteQuarter', async (id) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-tag-management/${id}`,
  });
  return res;
});

export const updateQuarter = createAsyncThunk('appraisal/updateQuarter', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `appraisal-tag-management/${data.appraisal_tag_id}`,
    data: data,
  });
  return res;
});

export const getAlltagnames = createAsyncThunk('appraisal/getAlltagnames', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-tag-management?&page=1&limit=no`,
  });
  return res;
});

export const createGoal = createAsyncThunk('appraisal/createGoal', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal`,
    data: data,
  });
  return res;
});

export const updateGoal = createAsyncThunk('appraisal/updateGoal', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal`,
    data: data,
  });
  return res;
});

export const bulkUploadGoals = createAsyncThunk('appraisal/bulkUploadGoals', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal?bulkUpload=true`,
    data: data,
  });
  return res;
});

export const getAllGoals = createAsyncThunk('appraisal/getAllGoals', async (data) => {
  const { user, tags, search, archiveViewChecked, fromDate, toDate, goalStatus, needsEditApproval, limit = 25, page = 1, type = 1 } = data;
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal?page=${page}&limit=${limit}&fromDate=${fromDate}&toDate=${toDate}&type=${type}&tag=${tags}&search=${search}&isArchieve=${archiveViewChecked}&goalStatus=${
      goalStatus == -1 ? null : goalStatus
    }&needsEditApproval=${needsEditApproval ? 'true' : ''}&userId=${user?.userID === -1 ? null : user?.userID}`,
  });
  return res;
});

// Reverse lookup for the Task <-> Goal-Job linkage: which Task Management
// task(s) a goal's jobs are backed by. Fetched on-demand per goal, not kept
// in global state - components consume it straight off the dispatch promise
// (see JobsSelectionArea.jsx), same as editJob/handleUpdate already does.
export const getSourceTasksForGoal = createAsyncThunk('appraisal/getSourceTasksForGoal', async (goalId) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal/${goalId}/source-tasks`,
  });
  return res;
});

export const getGoalStatusesForFilterTabs = createAsyncThunk('appraisal/getGoalStatusesForFilterTabs', async (data) => {
  const { user, tags, search, archiveViewChecked, fromDate, toDate, type = 1, goalStatus, limit = 25, page = 1 } = data;

  const res = await apiRequest({
    method: 'get',
    url: `/appraisal/count?fromDate=${fromDate}&toDate=${toDate}&type=${type}&tag=${
      tags == undefined ? null : tags
    }&search=${search}&isArchieve=${archiveViewChecked}&userId=${user?.userID === -1 ? null : user?.userID}`,
  });
  return res;
});

export const GoalFileUpload = createAsyncThunk('general/GoalFileUpload', async (formData) => {
  const res = await apiRequest({
    method: 'post',
    url: `general/upload`,
    data: formData,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return res;
});

export const MakeGoalApprovalRequest = createAsyncThunk('appraisal/MakeGoalApprovalRequest', async (extendedIds) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal/requestApproval/${extendedIds[0]}`,
  });
  return res;
});

export const singleOrBulkArchiveUnarchive = createAsyncThunk('appraisal/singleOrBulkArchiveUnarchive', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal/archive`,
    data: data,
  });
  return res;
});
export const extendGoalDate = createAsyncThunk('appraisal/extendGoalDate', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal/extend/${data?.id}`,
    data: data,
  });
  return res;
});

export const editJob = createAsyncThunk('appraisal/editJob', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `/goal-jobs-management/${data?.id}`,
    data: data,
  });
  return res;
});

export const addKra = createAsyncThunk('appraisal/addKra', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-library/${data.type}`,
    data: data,
  });
  return res;
});

export const editKra = createAsyncThunk('appraisal/editKra', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `appraisal-library/${data.appraisal_library_id}`,
    data: data,
  });
  return res;
});

export const deleteKra = createAsyncThunk('appraisal/deleteKra', async (data) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-library`,
    data: { data },
  });
  return data;
});

export const tagKra = createAsyncThunk('appraisal/tagKra', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-assignment`,
    data: data,
  });
  return res;
});

export const updateAssignedKra = createAsyncThunk('appraisal/updateAssignedKra', async (data) => {
  const res = await apiRequest({
    method: 'put',
    url: `appraisal-assignment/${data.type}`,
    data: data,
  });
  return res;
});

export const getAllDesignations = createAsyncThunk('appraisal/getAllDesignations', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `general/get-all-user-types`,
  });
  return res;
});

export const getAllKras = createAsyncThunk('appraisal/getAllKras', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-library?type=${data.type}&limit=no&search=${data.search}`,
  });
  return res;
});

export const getAssignedKra = createAsyncThunk('appraisal/getAssignedKra', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-assignment/get-usertype-based-data/${data.type}`,
  });
  return res;
});

export const deletAssignedKra = createAsyncThunk('appraisal/deletAssignedKra', async (data) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-assignment`,
    data: { data },
  });
  return res;
});

export const deleteAssignedByUserType = createAsyncThunk('appraisal/deleteAssignedByUserType', async (data) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-assignment/delete-by-userType/${data.usertypeId}`,
    data: data,
  });
  return res;
});

export const approveGoal = createAsyncThunk('appraisal/approveGoal', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal/approve`,
    data: data,
  });
  return res;
});

export const reviewEditRequest = createAsyncThunk('appraisal/reviewEditRequest', async ({ edit_request_id, decision, remarks }) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal/edit-requests/${edit_request_id}/review`,
    data: { decision, remarks },
  });
  return res;
});

export const getPendingEditRequestCount = createAsyncThunk('appraisal/getPendingEditRequestCount', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal/edit-requests/pending-count`,
  });
  return res;
});

export const bulkTagging = createAsyncThunk('appraisal/Bulktagging', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-assignment/bulk-upload/${data.type}`,
    data: data,
  });
  return res;
});

export const getSkillAndWllUserSpecific = createAsyncThunk('appraisal/getSkillAndWllUserSpecific', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal-assignment/${data.type}?userTypeId=${data.userTypeId}&userId=${data.userId}&page=${data.page}&limit=${data.limit}`,
  });
  return res;
});

export const getKraUserSpecific = createAsyncThunk('appraisal/getKraUserSpecific', async (data) => {
  const { user, type, userTypeId } = data;
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal-assignment/${type}?userTypeId=${userTypeId}&userId=${user?.userID ? user?.userID : ''}`,
  });
  return res;
});

export const getAllAssignedKra = createAsyncThunk('appraisal/getAllAssignedKra', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-assignment/${data.type}?userId=${data.userID}&page=${data.page}&limit=${data.limit}&search=${data.search}`,
  });
  return res;
});

export const addNewJob = createAsyncThunk('appraisal/addNewJob', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `goal-jobs-management/${data.goal_id}/${data.extendId}`,
    data: data,
  });
  return res;
});

export const getAllSelfAppraisalAssignedLists = createAsyncThunk(
  'getAllSelfAppraisalAssignedLists',
  async (dispatchParams) => {
    const { paginationData } = dispatchParams;
    const paginationUrl = getPaginationUrl(paginationData);
    const res = await apiRequest({
      method: 'get',
      url: `appraisal-cycles/user-specific/${paginationUrl}`,
    });
    return res;
  }
);

export const getAppraisalCyclicleFillingDatas = createAsyncThunk(
  'getAppraisalCyclicleFillingDatas',
  async (dispatchParams) => {
    const { id, userID, isMangerTrue } = dispatchParams;
    const res = await apiRequest({
      method: 'get',
      url: `appraisal/rating/${id}?userID=${userID || null}&isManager=${isMangerTrue || false}`,
    });
    return res;
  }
);

export const appraisalDataPost = createAsyncThunk('appraisal/appraisalDataPost', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal/rating/${data?.appraisalCycleId}`,
    data: data,
  });
  return res;
});

export const getAllMangerAppraisalCycleLists = createAsyncThunk(
  'getAllMangerAppraisalCycleLists',
  async (dispatchParams) => {
    const { paginationData } = dispatchParams;
    const paginationUrl = getPaginationUrl(paginationData);
    const res = await apiRequest({
      method: 'get',
      url: `appraisal/cycleView/${paginationUrl}&isReportingHeadView=true`,
    });
    return res;
  }
);

export const getAllUsersUnderAppraisalCycle = createAsyncThunk('getAllUsersUnderAppraisalCycle', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycles/users/${data?.cycleId}?manager=true`,
  });
  return res;
});

export const getAllEvaluationcycle = createAsyncThunk('appraisal/getAllEvaluationcycle', async () => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycles/all`,
  });
  return res;
});

export const startCycle = createAsyncThunk('appraisal/startCycle', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/start-cycle/${data.id}`,
    data: data,
  });
  return res;
});

export const cloneCycle = createAsyncThunk('appraisal/cloneCycle', async (id) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/clone/${id}`,
  });
  return res;
});

export const createCycle = createAsyncThunk('appraisal/createCycle', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-cycles`,
    data: data,
  });
  return res;
});

export const getCycleAssignedUsers = createAsyncThunk('appraisal/getCycleAssignedUsers', async (id) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycle-assignments/${id}?page=1&limit=no`,
  });
  return res;
});

export const excludeUser = createAsyncThunk('appraisal/excludeUser', async (ids) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-cycle-assignments`,
    data: ids,
  });
  return res;
});
export const assignuserToCycle = createAsyncThunk('appraisal/assignuserToCycle', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-cycle-assignments/${data.id}`,
    data: data,
  });
  return res;
});

export const getCyclePreData = createAsyncThunk('appraisal/getCyclePreData', async (id) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycles/${id}`,
  });
  return res;
});
export const editCycle = createAsyncThunk('appraisal/editCycle', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/edit/${data.id}`,
    data: data,
  });
  return res;
});

export const deleteCycle = createAsyncThunk('appraisal/deleteCycle', async (id) => {
  const res = await apiRequest({
    method: 'delete',
    url: `appraisal-cycles/${id}`,
  });
  return res;
});

export const changeReviewer = createAsyncThunk('appraisal/changeReviewer', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `/appraisal-assignment/change-reviewer/${data.id}`,
    data: data,
  });
  return res;
});

export const getCycleStatus = createAsyncThunk('appraisal/getCycleStatus', async (cycleId) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycles/status/${cycleId}`,
  });
  return res;
});
export const sendNotification = createAsyncThunk('appraisal/sendNotification', async (data) => {
  const res = await apiRequest({
    method: 'post',
    url: `appraisal-cycles/sendNotification/${data.cycleId}`,
    data: data,
  });
  return res;
});

export const publishCycle = createAsyncThunk('appraisal/publishCycle', async (cycleId) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/publish/${cycleId}`,
  });
  return res;
});

export const completeCycle = createAsyncThunk('appraisal/completeCycle', async (cycleId) => {
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/publish/${cycleId}`,
  });
  return res;
});

export const publishReport = createAsyncThunk('appraisal/publishReport', async (payload) => {
  // Accept either { id, status } or { appraisalAssignmentId, status }
  const id = payload?.id ?? payload?.appraisalCycleAssignmentId;
  const status = payload?.status;
  const res = await apiRequest({
    method: 'patch',
    url: `appraisal-cycles/updatePublishStatus?appraisalCycleAssignmentId=${id}&status=${status}`,
  });
  return res;
});

export const getManageAppraisalData = createAsyncThunk('appraisal/getManageAppraisalData', async (cycleId) => {
  const res = await apiRequest({
    method: 'get',
    url: `appraisal-cycles/statusDetails/${cycleId}`,
  });
  return res;
});

export const getReportingHeadSkillWillKra = createAsyncThunk('appraisal/getReportingHeadSkillWillKra', async (data) => {
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal-assignment/get-datas-under-reporting-head/${data.type}?page=${data.page}&limit=${data.limit}`,
  });
  return res;
});
// ---------------------- GOAL ADD BLOCK / UNBLOCK ---------------------------
// Admins block/unblock a user's "Add Goal" ability from
// Appraisal Operations > Config. These thunks back that screen and the
// disabled state of the "Add Goal" button on the Goal tab.

// List users (one page at a time) with their "blocked from adding goals" flag.
// Supports server-side search + pagination so the Config screen can lazy-load.
// `status` is 'all' | 'blocked' | 'allowed' and drives the filter tabs; the
// response also carries the count for each tab.
export const getGoalBlockUsers = createAsyncThunk('appraisal/getGoalBlockUsers', async (data = {}) => {
  const page = data?.page || 1;
  const limit = data?.limit || 20;
  const status = data?.status || 'all';
  const search = data?.search ? `&search=${encodeURIComponent(data.search)}` : '';
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal-goal-block/users?page=${page}&limit=${limit}&status=${status}${search}`,
  });
  return res;
});

// Block (isBlocked = true) or unblock (false) a single user.
export const toggleGoalBlockUser = createAsyncThunk('appraisal/toggleGoalBlockUser', async (data) => {
  const res = await apiRequest({
    method: 'patch',
    url: `/appraisal-goal-block/toggle`,
    data: { userId: data.userId, isBlocked: data.isBlocked },
  });
  return res;
});

// Whether a given user (defaults to the logged-in user) is blocked from adding goals.
export const getGoalBlockStatus = createAsyncThunk('appraisal/getGoalBlockStatus', async (data) => {
  const userId = data?.userId ? `?userId=${data.userId}` : '';
  const res = await apiRequest({
    method: 'get',
    url: `/appraisal-goal-block/status${userId}`,
  });
  return res;
});

const appraisalSlice = createSlice({
  name: 'appraisal',
  initialState,
  reducers: {
    sampleReducerAction: (state, action) => {
      console.log('Sample action triggered', action.payload);
    },
    clearUserBasedKraSkillWill:(state,action)=>{
      state.alltaggedkra=[]
    }
  },
  extraReducers: (builder) => {
    // ---- Goal add block/unblock config ----
    // The user list is handled locally in the Config component; here we only
    // track whether the current user is blocked (drives the Add Goal button).

    // Back to "unknown" while a new status is in flight. Without this the flag
    // keeps the PREVIOUS user's (or the previous page's) answer, so the Add Goal
    // button renders the stale state until the request lands.
    builder.addCase(getGoalBlockStatus.pending, (state) => {
      state.currentUserGoalBlocked = null;
    });
    builder.addCase(getGoalBlockStatus.fulfilled, (state, action) => {
      // On a failed call the API helper still resolves, with status "error".
      // Treat that as "allowed" rather than leaving the button dead forever —
      // the backend rejects a blocked user's save with a 403 either way.
      state.currentUserGoalBlocked =
        action.payload?.status === 'success' ? !!action.payload?.data?.isBlocked : false;
    });
    builder.addCase(getGoalBlockStatus.rejected, (state) => {
      state.currentUserGoalBlocked = false;
    });

    builder.addCase(getAllQuarters.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAllQuarters.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        quarters: action.payload,
      };
    });
    builder.addCase(getPendingEditRequestCount.fulfilled, (state, action) => {
      return {
        ...state,
        pendingEditRequestCount: action.payload?.data?.count || 0,
      };
    });
    builder.addCase(createQuarter.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createQuarter.fulfilled, (state, action) => {
      state.loading = false;
    });
    builder.addCase(deleteQuarter.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteQuarter.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------update qr-----------------------------
    builder.addCase(updateQuarter.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateQuarter.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ----------------------get all tags no limit-------------------------------
    builder.addCase(getAlltagnames.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAlltagnames.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        tagNames: action.payload.data,
      };
    });
    builder.addCase(createGoal.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(createGoal.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -----------------------------bulk upload----------------------------------
    builder.addCase(bulkUploadGoals.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(bulkUploadGoals.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------GETING ALL GOALS-----------------------------
    builder.addCase(getAllGoals.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllGoals.fulfilled, (state, action) => {
      state.loading = false;
      state.goalData.data = action.payload?.data || [];
      state.goalData.meta = action.payload?.meta || {};
    });
    // ---------------------------GOAL FILE UPLOAD-----------------------------
    builder.addCase(GoalFileUpload.pending, (state, action) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(GoalFileUpload.fulfilled, (state, action) => {
      const data = action.payload?.data;
      console.log('GoalFileUpload', data);

      return {
        ...state,
        fileName: data,
        loading: false,
      };
    });
    // ---------------------------GETING ALL GOAL STATUSES FOR FILTER TABS-----------------------------
    builder.addCase(getGoalStatusesForFilterTabs.pending, (state) => {
      state.goalStatusFilterTabLoading = true;
    });
    builder.addCase(getGoalStatusesForFilterTabs.fulfilled, (state, action) => {
      state.goalStatusFilterTabLoading = false;
      state.goalStatuses = action.payload?.data || initialState.goalStatuses;
    });
    // ---------------------------MAKE REQUEST FOR GOAL APPROVAL----------------------------
    builder.addCase(MakeGoalApprovalRequest.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(MakeGoalApprovalRequest.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------BULK OR SINGLE ACHIVE----------------------------
    builder.addCase(singleOrBulkArchiveUnarchive.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(singleOrBulkArchiveUnarchive.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------EXTEND THE GOAL DATE ----------------------------
    builder.addCase(extendGoalDate.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(extendGoalDate.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------EDIT THE JOB ----------------------------
    builder.addCase(editJob.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editJob.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ----------------approveGoal-------------------------------------------
    builder.addCase(approveGoal.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(approveGoal.fulfilled, (state, action) => {
      state.loading = false;
    });
    //--------------------------------add kra ------------------------------------
    builder.addCase(addKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------------- edit kra-------------------------------
    builder.addCase(editKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(editKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ----------------------------GET DESIGNATIONS--------------------------
    builder.addCase(getAllDesignations.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAllDesignations.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        designations: action.payload.data,
      };
    });
    // -------------------------getSkillAndWllUserSpecific--------------------------
    builder.addCase(getSkillAndWllUserSpecific.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getSkillAndWllUserSpecific.fulfilled, (state, action) => {
      const data = action.payload;
      return {
        ...state,
        loading: false,
        skillAndWllUserSpecificData: data,
      };
    });
    // ------------------------------GET KRAS-----------------------------------
    builder.addCase(getAllKras.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAllKras.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        kralibraries: action.payload.data,
      };
    });
    // ------------------------------- DELETE KRA--------------------------------
    builder.addCase(deleteKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -------------------------------- TAG KRA TO DESIGNATION---------------------------------------
    builder.addCase(tagKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(tagKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ------------------------ GETTING ASSIGNED KRAS----------------------------------------------.
    builder.addCase(getAssignedKra.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAssignedKra.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        taggedKra: action.payload.data,
        lastTaggedKraType: action?.meta?.arg?.type ?? null,
      };
    });
    // ------------------- DELETE ASSIGNED KRA-----------------------------------
    builder.addCase(deletAssignedKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deletAssignedKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -------------------------------------- edit goal-----------------------------
    builder.addCase(updateGoal.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateGoal.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -------- delete by userType------------------------------
    builder.addCase(deleteAssignedByUserType.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(deleteAssignedByUserType.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ------------------------ BULK TAGGING KRA------------------------------
    builder.addCase(bulkTagging.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(bulkTagging.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -------------------------getKraUserSpecific--------------------------
    builder.addCase(getKraUserSpecific.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getKraUserSpecific.fulfilled, (state, action) => {
      const data = action.payload;
      return {
        ...state,
        loading: false,
        kraUserSpecificData: data,
      };
    });
    builder.addCase(getAllAssignedKra.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAllAssignedKra.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        alltaggedkra: action.payload,
      };
    });
    // ---------------------- ADD NEW JOB------------------------------------
    builder.addCase(addNewJob.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(addNewJob.fulfilled, (state, action) => {
      state.loading = false;
    });
    // -------------------------------getAllEvaluationcycle-------------------------------
    builder.addCase(getAllEvaluationcycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getAllEvaluationcycle.fulfilled, (state, action) => {
      const data = action.payload.data;
      return {
        ...state,
        loading: false,
        evaluationData: data,
      };
    });
    // ---------------------startCycle-------------------------------------------
    builder.addCase(startCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(startCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ---------------------cloneCycle----------------------------------------
    builder.addCase(cloneCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(cloneCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------createCycle----------------------------
    builder.addCase(createCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(createCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------------getCycleAssignedUsers--------------------------------
    builder.addCase(getCycleAssignedUsers.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getCycleAssignedUsers.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        cycleAssignedUsers: action.payload.data,
      };
    });
    // ------------------excludeUser--------------------------
    builder.addCase(excludeUser.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(excludeUser.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ---------------------------assignuserToCycle----------------------------
    builder.addCase(assignuserToCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(assignuserToCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------getCyclePreData-----------------------------------
    builder.addCase(getCyclePreData.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getCyclePreData.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
        cyclePreData: action.payload.data,
      };
    });
    // -------------------editCycle---------------------------------
    builder.addCase(editCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(editCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -------------------deleteCycle-----------------------
    builder.addCase(deleteCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(deleteCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------------------changeReviewer-----------------------------

    builder.addCase(changeReviewer.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(changeReviewer.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -----------------------getCycleStatus------------------------------
    builder.addCase(getCycleStatus.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getCycleStatus.fulfilled, (state, action) => {
      const data = action.payload.data;
      return {
        ...state,
        loading: false,
        cycleStatus: data,
      };
    });
    // --------------------------sendNotification-----------------------------

    builder.addCase(sendNotification.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(sendNotification.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // -------------------------publishCycle--------------------------------

    builder.addCase(publishCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(publishCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ----------------------------completeCycle------------------------------------

    builder.addCase(completeCycle.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(completeCycle.fulfilled, (state, action) => {
      return {
        ...state,
        loading: false,
      };
    });
    // ------------------getManageAppraisalData--------------------------------
    builder.addCase(getManageAppraisalData.pending, (state) => {
      return {
        ...state,
        loading: true,
      };
    });
    builder.addCase(getManageAppraisalData.fulfilled, (state, action) => {
      // The backend can return an error body (e.g. missing cycle config -> 500) instead
      // of the expected array. In that case action.payload.data is an object like {}.
      // Only keep it if it is really an array; otherwise fall back to an empty array so
      // the UI just shows "no data" instead of crashing on manageAppraisalData.map().
      const data = Array.isArray(action.payload?.data) ? action.payload.data : [];
      return {
        ...state,
        loading: false,
        manageAppraisalData: data,
      };
    });
    // -----------------EDIT ASSIGNED KRA----------------------------
    builder.addCase(updateAssignedKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(updateAssignedKra.fulfilled, (state, action) => {
      state.loading = false;
    });
    // ---------------------- GET ALL SELF ASSIGND APPRAISAL DATA------------------------------------
    builder.addCase(getAllSelfAppraisalAssignedLists.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllSelfAppraisalAssignedLists.fulfilled, (state, action) => {
      return {
        ...state,
        selfAppraisalDatas: {
          ...state.selfAppraisalDatas,
          data: action?.payload?.data,
          meta: action?.payload?.meta,
        },
        loading: false,
      };
    });
    // ---------------------- GET  SELF APPRAISING FILLING DATAS-----------------------------------
    builder.addCase(getAppraisalCyclicleFillingDatas.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAppraisalCyclicleFillingDatas.fulfilled, (state, action) => {
      return {
        ...state,
        formFillingDatas: action?.payload?.data || {},
        loading: false,
      };
    });
    // -----------------ADD APPRAISAL DATA----------------------------
    builder.addCase(appraisalDataPost.pending, (state) => {
      state.appraisalPostLoading = true;
    });
    builder.addCase(appraisalDataPost.fulfilled, (state, action) => {
      state.appraisalPostLoading = false;
    });

    // ---------------------- GET ALL MANAGER CYCLE LISTS------------------------------------
    builder.addCase(getAllMangerAppraisalCycleLists.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllMangerAppraisalCycleLists.fulfilled, (state, action) => {
      return {
        ...state,
        apprasialCycleListsForManagers: {
          ...state.apprasialCycleListsForManagers,
          data: action?.payload?.data,
          meta: action?.payload?.meta,
        },
        loading: false,
      };
    });
    // ---------------------- GET ALL USERS LIST UNDER THE CORRESPONDING APPRAISAL CYCLE ------------------------------------
    builder.addCase(getAllUsersUnderAppraisalCycle.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllUsersUnderAppraisalCycle.fulfilled, (state, action) => {
      return {
        ...state,
        usersList: action?.payload?.data || [],
        loading: false,
      };
    });
    // -------------------------getReportingHeadSkillWillKra-----------------------

    builder.addCase(getReportingHeadSkillWillKra.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getReportingHeadSkillWillKra.fulfilled, (state, action) => {
      return {
        ...state,
        reportingHeadSkillWillKra: action?.payload?.data || [],
        loading: false,
      };
    });

    // ==================== -------------------publishReport---------------------------------------------------

    builder.addCase(publishReport.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(publishReport.fulfilled, (state, action) => {
      state.loading = false;
    });
  },
});

export const { sampleReducerAction,clearUserBasedKraSkillWill } = appraisalSlice.actions;
export default appraisalSlice.reducer;
